import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import {
  DEFAULT_TIMESHEET_SETTINGS,
  type TimesheetSettings,
} from '../types/timesheets'

export function normalizeTimesheetSettings(raw: unknown): TimesheetSettings {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_TIMESHEET_SETTINGS }
  }
  const data = raw as Partial<TimesheetSettings>
  return {
    autoSources: {
      sessions: data.autoSources?.sessions === true,
      meetings: data.autoSources?.meetings === true,
      focus: data.autoSources?.focus === true,
    },
    weekStart: data.weekStart === 'SUNDAY' ? 'SUNDAY' : 'MONDAY',
    rounding:
      data.rounding === '5_MIN' ||
      data.rounding === '6_MIN' ||
      data.rounding === '15_MIN'
        ? data.rounding
        : 'NONE',
    overtime: {
      dailyMinutes:
        typeof data.overtime?.dailyMinutes === 'number' && data.overtime.dailyMinutes >= 0
          ? data.overtime.dailyMinutes
          : 0,
      weeklyMinutes:
        typeof data.overtime?.weeklyMinutes === 'number' && data.overtime.weeklyMinutes >= 0
          ? data.overtime.weeklyMinutes
          : 0,
      multiplier:
        typeof data.overtime?.multiplier === 'number' && data.overtime.multiplier > 0
          ? data.overtime.multiplier
          : 1.5,
    },
    expectedWeeklyMinutes:
      typeof data.expectedWeeklyMinutes === 'number' && data.expectedWeeklyMinutes >= 0
        ? data.expectedWeeklyMinutes
        : 2400,
    gps: data.gps === true,
    reminders: {
      submitReminder: data.reminders?.submitReminder === true,
      approvalReminder: data.reminders?.approvalReminder === true,
    },
  }
}

const workspaceDocRef = (db: Firestore, workspaceId: string) =>
  doc(db, 'workspaces', workspaceId)

export async function getTimesheetSettings(
  workspaceId: string,
  options: { db?: Firestore } = {},
): Promise<TimesheetSettings> {
  const ref = workspaceDocRef(options.db ?? firestoreDb, workspaceId)
  const snap = await getDoc(ref)
  if (!snap.exists()) {
    return { ...DEFAULT_TIMESHEET_SETTINGS }
  }
  const data = snap.data() as { timesheetSettings?: unknown }
  return normalizeTimesheetSettings(data.timesheetSettings)
}

export function subscribeTimesheetSettings(
  workspaceId: string,
  onChange: (settings: TimesheetSettings) => void,
  onError?: (err: Error) => void,
  options: { db?: Firestore } = {},
): Unsubscribe {
  const ref = workspaceDocRef(options.db ?? firestoreDb, workspaceId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        onChange({ ...DEFAULT_TIMESHEET_SETTINGS })
        return
      }
      const data = snap.data() as { timesheetSettings?: unknown }
      onChange(normalizeTimesheetSettings(data.timesheetSettings))
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export async function updateTimesheetSettings(
  workspaceId: string,
  settings: Partial<TimesheetSettings>,
  options: { db?: Firestore } = {},
): Promise<void> {
  const ref = workspaceDocRef(options.db ?? firestoreDb, workspaceId)
  const current = await getTimesheetSettings(workspaceId, options)
  const merged: TimesheetSettings = normalizeTimesheetSettings({
    ...current,
    ...settings,
    autoSources: {
      ...current.autoSources,
      ...settings.autoSources,
    },
    overtime: {
      ...current.overtime,
      ...settings.overtime,
    },
    reminders: {
      ...current.reminders,
      ...settings.reminders,
    },
  })

  await updateDoc(ref, {
    timesheetSettings: merged,
    updatedAt: serverTimestamp(),
  })
}
